import { ReactNode } from "react";
import Card from "@rio-cloud/rio-uikit/Card";
import { useT } from "@hooks";
import { resolveKpiDelta } from "@utils/kpiDelta";
import KpiComparisonTooltip from "@components/shared/KpiComparisonTooltip";
import type { KpiComparison } from "@types";

export const KPI_ICON_WRAPPER_STYLE = { width: 56, height: 50, borderRadius: 6 } as const;
export const KPI_ICON_STYLE = { fontWeight: 400 } as const;
export const KPI_VALUE_STYLE = { fontWeight: 400, fontSize: 24 } as const;
export const KPI_FOOTER_STYLE = { fontSize: 15 } as const;

export const CURRENCY_KPI_ICON = (
  <span className="text-size-h2 text-medium text-color-primary" style={KPI_ICON_STYLE}>
    R$
  </span>
);

export const calculateDelta = (current: number, previous?: number): number | null => {
  if (previous === undefined || previous === null || previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
};

export interface TransactionKpiCardProps {
  label: string;
  value: string;
  icon: ReactNode;
  tooltip?: ReactNode;
  deltaPercentage?: number | null;
  comparison?: KpiComparison | null;
  formatComparisonValue?: (value: number) => string;
}

const TransactionKpiCard = ({
  label,
  value,
  icon,
  tooltip,
  deltaPercentage,
  comparison,
  formatComparisonValue,
}: TransactionKpiCardProps) => {
  const t = useT();
  const delta = resolveKpiDelta(comparison ? comparison.variationPercent : deltaPercentage);

  return (
    <Card padding={0} className="overflow-hidden display-flex flex-column height-100pct">
      <div className="padding-15 flex-1-1">
        <div className="display-flex align-items-center gap-10 gap-20-sm">
          <div
            className="display-flex align-items-center justify-content-center flex-0 bg-lightest"
            style={KPI_ICON_WRAPPER_STYLE}
          >
            {icon}
          </div>
          <div className="display-flex flex-column gap-2 min-width-0">
            <span className="text-size-18 text-color-gray">
              {label}
              {tooltip}
            </span>
            <span style={KPI_VALUE_STYLE}>{value}</span>
          </div>
        </div>
      </div>
      <div
        className="display-flex flex-wrap align-items-center bg-lightest padding-y-10 padding-x-20"
        style={KPI_FOOTER_STYLE}
      >
        <KpiComparisonTooltip comparison={comparison} formatValue={formatComparisonValue ?? String}>
          <span className={`display-flex align-items-center gap-2 text-medium ${delta.colorClass}`}>
            <span className={`rioglyph ${delta.iconClass}`} />
            {Math.abs(delta.value).toFixed(0)}%
          </span>
          <span className="text-color-gray">{t("kpi.vsLastMonth")}</span>
        </KpiComparisonTooltip>
      </div>
    </Card>
  );
};

export default TransactionKpiCard;
